import { useEffect, useState, useCallback } from 'react';
import { IoIosCloseCircle } from "react-icons/io";
import PropTypes from 'prop-types';
import { themes } from '../../lib/theme';
import { getSubdomain } from '../../lib/utils';
import { CgSpinner } from "react-icons/cg";
import { getDocumentTypes, uploadDocuments } from '../../api/api';
import useTheme from '../../hooks/useTheme';
import { BiSolidBadgeCheck, BiSolidError } from 'react-icons/bi';
import { useTranslation } from 'react-i18next';
import {
  MAX_NOTE_LENGTH,
  handleFileSelection
} from './uploadHandlers';

const DocumentUploadModal = ({ isOpen, onClose, accountID }) => {
  const { t, i18n } = useTranslation();
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [docType, setDocType] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [success, setSuccess] = useState({});
  const [currentTenant, setCurrentTenant] = useState('');
  const [errorMessages, setErrorMessages] = useState([]);
  const [allUploadsComplete, setAllUploadsComplete] = useState(false);
  const [uploadStats, setUploadStats] = useState({ success: 0, failed: 0 });

  useTheme();

  // Map API error messages to translation keys
  const translateApiError = useCallback((apiErrorMessage) => {
    if (!apiErrorMessage) return t('uploadDocs.unexpectedError');

    const errorMap = {
      'Invalid Document Extension': 'uploadDocs.apiErrors.invalidDocumentExtension',
      'Invalid Document Type': 'uploadDocs.apiErrors.invalidDocumentType',
      'Invalid Document Size': 'uploadDocs.apiErrors.invalidDocumentSize',
      'Invalid Document Account': 'uploadDocs.apiErrors.invalidDocumentAccount',
      'Invalid Document Name': 'uploadDocs.apiErrors.invalidDocumentName',
      'Invalid Document Note': 'uploadDocs.apiErrors.invalidDocumentNote',
      'Invalid Account Number': 'uploadDocs.apiErrors.invalidAccountNumber',
      'Failed to attach file to account.': 'uploadDocs.apiErrors.attachDocumentFailure',
      'Virus detected in the file. Please upload a clean file.': 'uploadDocs.apiErrors.virusDetected',
      'Error encountered while scanning the file.': 'uploadDocs.apiErrors.scanningError'
    };

    const translationKey = errorMap[apiErrorMessage];
    return translationKey ? t(translationKey) : apiErrorMessage;
  }, [t]);

  const translateDocType = (docType) => {
    if (!docType) return '';
    
    // Use a simple lookup approach
    try {
      // If docType has a colon, let's handle it specially
      if (docType.includes(':')) {
        // For keys with colons, check direct object mappings in combined.json
        // const languages = {en: 'en', fr: 'fr'};
        const currentLang = i18n.language === 'fr' ? 'fr' : 'en'; // Simple language detection
        console.log(currentLang)
        // Reference the hard-coded mapping for specific document types with colons
        const specialCases = {
          "Stat Dec re: No Secondary Financing": {
            "en": "Stat Dec re: No Secondary Financing",
            "fr": "Déclaration statutaire: aucun financement secondaire"
          },
          "Statutory Declaration re: Building Loan": {
            "en": "Statutory Declaration re: Building Loan",
            "fr": "Déclaration statutaire: prêt de construction"
          },
          "Statutory Declaration re: Marital Status/Spousal Consent": {
            "en": "Statutory Declaration re: Marital Status/Spousal Consent",
            "fr": "Déclaration statutaire: statut matrimonial / consentement"
          },
          "Statutory Declaration re: O/O": {
            "en": "Statutory Declaration re: O/O",
            "fr": "Déclaration statutaire: propriétaire occupant"
          },
          "Statutory Declaration re: O/O & Tenanted": {
            "en": "Statutory Declaration re: O/O & Tenanted",
            "fr": "Déclaration statutaire: proprio occupant & locataire"
          },
          "Statutory Declaration re: Other Properties": {
            "en": "Statutory Declaration re: Other Properties",
            "fr": "Déclaration statutaire: autres propriétés"
          },
          "Statutory Declaration re: Tenanted": {
            "en": "Statutory Declaration re: Tenanted",
            "fr": "Déclaration statutaire: locataire"
          },
          "Statutory Declaration re: UFFI": {
            "en": "Statutory Declaration re: UFFI",
            "fr": "Déclaration statutaire: UFFI"
          },
          "Appraisal: Purchase + (As is and As Complete)": {
            "en": "Appraisal: Purchase + (As is and As Complete)",
            "fr": "Évaluation: Achat + (tel quel et complété)"
          },
          "Bank Statements: 3 mths confirming Payroll Deposits.": {
            "en": "Bank Statements: 3 mths confirming Payroll Deposits.",
            "fr": "Relevés bancaires: 3 mois confirmant dépôts de paie"
          },
          "Bank Statements: 3mths, confimring initial Deposit": {
            "en": "Bank Statements: 3mths, confimring initial Deposit",
            "fr": "Relevés bancaires: 3 mois, dépôt initial confirmé"
          },
        };
        
        // If it's a special case, return the appropriate translation
        if (specialCases[docType]) {
          return specialCases[docType][currentLang] || docType;
        }
      }
      
      // For everything else, use the standard approach
      const translationKey = `apiTranslations.docUploadTypes.${docType}`;
      const translatedValue = t(translationKey);
      
      // Make sure we return a string, not an object
      return (typeof translatedValue === 'string' && translationKey !== translatedValue) 
        ? translatedValue 
        : docType;
    } catch (error) {
      console.warn('Translation error for', docType, error);
      return docType;
    }
  };

  useEffect(() => {
    const tenant = sessionStorage.getItem("tenantName");
    setCurrentTenant(tenant || '');

    // Fetch document types when the modal is opened
    const fetchDocTypes = async () => {
      try {
        const apiData = await getDocumentTypes();
        if (apiData?.attachedDocumentTypes) {
          // Sort the document types based on their translated values
          const sortedTypes = [...apiData.attachedDocumentTypes].sort((a, b) => {
            const aTranslated = translateDocType(a.keyName);
            const bTranslated = translateDocType(b.keyName);
            return aTranslated.localeCompare(bTranslated, i18n.language, { 
              sensitivity: 'base',
              ignorePunctuation: true
            });
          });
          
          const keyNames = sortedTypes.map(item => ({
            value: item.keyName,
            label: translateDocType(item.keyName)
          }));
          
          setDocType(keyNames);
        }
      } catch (error) {
        setErrorMessages(prev => [...prev, t('uploadDocs.fetchTypeError'), error]);
      }
    };

    if (isOpen) {
      fetchDocTypes();
    }
  }, [isOpen, t]);

  useEffect(() => {
    if (!isOpen) {
      // Reset state when modal is closed
      setSelectedFiles([]);
      setUploading(false);
      setSuccess({});
      setErrorMessages([]);
    }
  }, [isOpen]);

  const handleFileInputChange = useCallback(async (event) => {
    if (uploading) return;

    const files = Array.from(event.target.files || []);
    await handleFileSelection({
      files,
      selectedFiles,
      accountID,
      t,
      setErrorMessages,
      setSelectedFiles
    });
  }, [uploading, selectedFiles, accountID, t]);

  const handleRemoveDocument = useCallback((index) => {
    const updatedFiles = [...selectedFiles];
    updatedFiles.splice(index, 1);
    setSelectedFiles(updatedFiles);
  }, [selectedFiles]);

  const handleSelectChange = useCallback((index, value) => {
    const updatedFiles = [...selectedFiles];
    updatedFiles[index].docType = value;
    setSelectedFiles(updatedFiles);
  }, [selectedFiles]);

  const handleNotesChange = useCallback((index, value) => {
    if (value.length <= MAX_NOTE_LENGTH) {
      const updatedFiles = [...selectedFiles];
      updatedFiles[index].note = value;
      setSelectedFiles(updatedFiles);
    }
  }, [selectedFiles]);

  const handleSendDocuments = async () => {
    if (!selectedFiles.length) {
      setErrorMessages([t('uploadDocs.noFilesSelected')]);
      return;
    }

    const invalidFiles = selectedFiles.filter(file => !file.docType);
    if (invalidFiles.length) {
      setErrorMessages(prev => [...prev, t('uploadDocs.selectDocType')]);
      return;
    }

    setErrorMessages([]);
    setUploading(true);
    setUploadStats({ success: 0, failed: 0 });

    try {
      for (let i = 0; i < selectedFiles.length; i++) {
        const document = selectedFiles[i];
        
        setSuccess(prev => ({
          ...prev,
          [i]: { neutral: true, yes: false, no: false }
        }));

        try {
          const result = await uploadDocuments(document);
          if (result?.isSuccess) {
            setUploadStats(prev => ({ ...prev, success: prev.success + 1 }));
            setSuccess(prev => ({
              ...prev,
              [i]: { neutral: false, yes: true, no: false }
            }));
          } else {
            setUploadStats(prev => ({ ...prev, failed: prev.failed + 1 }));
            setSuccess(prev => ({
              ...prev,
              [i]: { neutral: false, yes: false, no: true }
            }));
            const translatedError = translateApiError(result.message);
            const errorMessage = t('uploadDocs.uploadFailed', {
              fileName: document.fileName,
              error: translatedError
            });
            setErrorMessages(prev => [...prev, errorMessage]);
          }
        } catch (error) {
          setUploadStats(prev => ({ ...prev, failed: prev.failed + 1 }));
          setSuccess(prev => ({
            ...prev,
            [i]: { neutral: false, yes: false, no: true }
          }));
          const translatedError = translateApiError(error.message);
          const errorMessage = t('uploadDocs.uploadFailed', {
            fileName: document.fileName,
            error: translatedError
          });
          setErrorMessages(prev => [...prev, errorMessage]);
        }
      }
    } finally {
      setUploading(false);
      setAllUploadsComplete(true);
    }
  };

  const handleDone = () => {
    onClose();
    // window.location.reload();
  };

  const handleClear = () => {
    setSelectedFiles([]);
  };

  useEffect(() => {
    const subdomain = getSubdomain();
    const theme = themes[subdomain] || themes.default;
    const button = document.getElementById("docUploadBtn");
    if (button) {
      button.style.backgroundColor = theme.primaryButtonBgColor;
      button.style.color = theme.primaryButtonTextColor;
    }
  }, []);

  useEffect(() => {
    if (errorMessages.length > 0) {
      const timer = setTimeout(() => {
        setErrorMessages([]);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [errorMessages]);

  return (
    <div className={`document-upload-modal ${isOpen ? 'flex' : 'hidden'} fixed inset-0 items-center justify-center z-50`}>
      <div className="modal-backdrop absolute bg-black/50 inset-0" onClick={uploading ? undefined : onClose}></div>

      <div className="modal-content rounded-lg absolute max-w-[20rem] md:min-w-fit p-10 overflow-auto bg-gray-50 z-50">
        <div className="modal-header flex justify-between items-center">
          <h2 className='text-zinc-500'>{t('uploadDocs.headname')}</h2>
          <button 
            className='text-red-500 bg-transparent border-none hover:text-red-600 p-0' 
            onClick={onClose}
            disabled={uploading}
          >
            <IoIosCloseCircle size={30}/>
          </button>
        </div>

        {allUploadsComplete ? (
          <div className="text-center py-6">
            <BiSolidBadgeCheck className="mx-auto text-green-500 text-5xl mb-4" />
            <h3 className="text-xl font-semibold mb-2">Upload Complete</h3>
            <p className="text-gray-600 mb-4">
              Successfully uploaded: {uploadStats.success} files
              {uploadStats.failed > 0 && ` (${uploadStats.failed} failed)`}
            </p>
            <button
              onClick={handleDone}
              className="themeButton py-2 px-6"
            >
              Done
            </button>
          </div>
        ) : (
          <>
            <span className='border-2 border-black/20 rounded-lg relative my-4 flex items-center cursor-pointer transition-all duration-150 hover:bg-gray-300 bg-gray-100'>
              <span className='absolute text-center text-black/60 w-full cursor-pointer z-40'>
                {t('uploadDocs.input')}
              </span>
              <input 
                type="file" 
                multiple 
                onChange={handleFileInputChange} 
                className="w-full cursor-pointer z-50 opacity-0"
                disabled={uploading}
              />
            </span>

            <div
              className="border flex items-center border-dashed border-zinc-500 rounded-lg p-4 mb-4"
              onDrop={(e) => {
                e.preventDefault();
                if (!uploading) handleFileInputChange({ target: { files: e.dataTransfer.files } });
              }}
              onDragOver={(e) => e.preventDefault()}
            >
              <span className='w-full text-center text-zinc-500'>
                {t('uploadDocs.dragone')}<br /> or <br />{t('uploadDocs.dragtwo')}
              </span>
            </div>

            {selectedFiles.length > 0 && !uploading && (
              <div className="selected-documents max-h-[25rem] overflow-auto">
                {selectedFiles.map((file, index) => (
                  <div key={index} className="document-info flex flex-col items-center border p-2 rounded-lg justify-between gap-2 mb-2">
                    <div className='flex items-center justify-between gap-2'>
                      <span>{file.fileName.slice(0,10)}...</span>
                      {/* <select
                        id="docTypeSelect"
                        onChange={(e) => handleSelectChange(index, e.target.value)}
                        className="py-3 max-w-[220px] rounded-lg"
                      >
                        <option value="">{t('uploadDocs.docTypes')}</option>
                        {docType.map((typeName, idx) => (
                          <option key={idx} value={typeName}>
                            {typeName}
                          </option>
                        ))}
                      </select> */}
                      <select
                        id="docTypeSelect"
                        onChange={(e) => handleSelectChange(index, e.target.value)}
                        className="py-3 max-w-[220px] rounded-lg"
                      >
                        <option value="">{t('uploadDocs.docTypes')}</option>
                        {docType.map((type, idx) => (
                          <option key={idx} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                      <button 
                        onClick={() => handleRemoveDocument(index)} 
                        className="themeButton bg-red-500 text-white p-1 rounded-lg"
                      >
                        {t('uploadDocs.remove')}
                      </button>
                    </div>
                    <div className='w-full'>
                      <textarea
                        type="textarea"
                        placeholder="Note"
                        value={file.note}
                        onChange={(e) => handleNotesChange(index, e.target.value)}
                        className="border p-1 rounded-lg min-w-full"
                        maxLength={MAX_NOTE_LENGTH}
                      />
                      <div className="text-right text-xs text-gray-500">
                        {`${file.note.length}/${MAX_NOTE_LENGTH} ${t('uploadDocs.characters')}`}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {selectedFiles.length > 0 && uploading && (
              <div className="selected-documents max-h-52 overflow-auto">
                {selectedFiles.map((file, index) => (
                  <div key={index} className="document-info flex items-center border p-2 rounded-lg justify-between gap-2 mb-2 transition-all ease-linear">
                    <span>{file.fileName.slice(0,10)}...</span>
                    {success[index] && (
                      <>
                        {success[index].yes && <span className="flex items-center gap-2 text-green-500">{t('uploadDocs.success')}<BiSolidBadgeCheck /></span>}
                        {success[index].no && <span className="flex items-center gap-2 text-red-500">{t('uploadDocs.failed')}<BiSolidError /></span>}
                        {success[index].neutral && <span className="flex items-center gap-2 text-gray-500">{t('uploadDocs.uploading')}<CgSpinner className="animate-spin" /></span>}
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="buttons-section w-full flex flex-col md:flex-row justify-center items-center mt-5 gap-4">
              {selectedFiles.length > 0 ? (
                <>
                  <button 
                    onClick={handleClear} 
                    className="bg-gray-500 hover:bg-gray-700 text-white py-2 px-4 rounded-lg"
                    disabled={uploading}
                  >
                    {t('uploadDocs.clear')}
                  </button>
                  <button 
                    onClick={handleSendDocuments} 
                    id='docUploadBtn' 
                    className="themeButton py-2 px-4 rounded-lg"
                    disabled={uploading}
                  >
                    {uploading ? (
                      <span className="flex items-center gap-2">
                        <CgSpinner className="animate-spin" />
                        {t('uploadDocs.uploading')}
                      </span>
                    ) : (
                      `${t('uploadDocs.uploadBtn')} ${currentTenant.toUpperCase()}`
                    )}
                  </button>
                </>
              ) : (
                <button 
                  onClick={onClose} 
                  className="bg-gray-500 hover:bg-gray-700 text-white py-2 px-4 rounded-lg"
                  disabled={uploading}
                >
                  {t('uploadDocs.cancel')}
                </button>
              )}
            </div>
          </>
        )}

        {/* Error Messages */}
        <div className="fixed bottom-4 right-4 w-80 space-y-2">
          {errorMessages.map((message, index) => (
            <div 
              key={index}
              className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded shadow-md flex justify-between items-center transform transition-all duration-300 ease-in-out"
              style={{
                animation: 'slideIn 0.3s ease-out forwards',
              }}
            >
              <p className="text-sm">{message}</p>
              <span
                onClick={() => setErrorMessages(prev => prev.filter((_, i) => i !== index))}
                className="text-red-700 hover:text-red-900 focus:outline-none cursor-pointer"
              >
                <IoIosCloseCircle size={20} />
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

DocumentUploadModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  accountID: PropTypes.string.isRequired,
};

export default DocumentUploadModal;